/** Your calls: the hours roll up, then your longest call plays out along its own length. */

import { Scene, Zone, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import type { You } from "../../types";
import { count } from "../shared";

const BLOBS: BlobSpec[] = [
  { colour: "yellow", x: 1000, y: 330, r: 150, seed: 16.3, wob: 0.22 },
  { colour: "ink", x: 80, y: 1840, r: 240, seed: 17.1, wob: 0.18, opacity: 0.18 },
];

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// The lane's track inside the panel, in panel pixels.
const TRACK_X = 150;
const TRACK_W = 738;
const LANE_Y = 470;

/** "212", or "2.5" for a few hours. */
const hoursText = (h: number) => (h >= 10 || Number.isInteger(h) ? count(Math.round(h)) : h.toFixed(1));

/** "6 h 11 min", or "42 min" for a short one. */
function duration(minutes: number) {
  const m = Math.round(minutes);
  return m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} min` : `${m} min`;
}

/** "Sat 21 Feb". */
function dayOf(iso: string) {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return `${WEEKDAYS[new Date(y, m - 1, d).getDay()]} ${d} ${MONTHS[m - 1]}`;
}

/** Axis marks from the call's start to its end: at most eight, on round steps. */
function marks(minutes: number) {
  const step = [5, 10, 15, 30, 60, 120, 180, 240, 360, 480, 720, 1440].find((s) => minutes / s <= 7) ?? 1440;
  const out: { at: number; label: string }[] = [];
  for (let m = 0; m <= minutes; m += step) {
    out.push({ at: m / minutes, label: m === 0 ? "0" : m < 60 ? `${m} min` : `${m / 60} h` });
  }
  return out;
}

/** The hours' digit size: the video's, smaller when a big number needs the room. */
function bigSize(text: string) {
  const digits = text.replace(/[,.]/g, "").length;
  return Math.min(380, Math.floor(616 / (digits * 0.45 + (text.length - digits) * 0.22)));
}

export function Calls({ calls }: { calls: NonNullable<You["calls"]> }) {
  const hours = hoursText(calls.hours);
  const decimal = hours.includes(".");
  const size = bigSize(hours);
  // A call with no length has nothing to play out, so the panel stays away.
  const longest = calls.longest_minutes > 0 ? calls.longest_minutes : 0;

  const root = useTimeline((s) => {
    s.rise("[data-part=kick]", 0.35, { y: 0 });
    s.rise(".c-row", 0.4, { y: 40, sound: false });
    s.count(s.q(".c-big")[0], calls.hours, 0.45, 1.4, decimal ? (v) => v.toFixed(1) : undefined, { ease: "power3.out" });
    s.rise("[data-part=sub]", 1.2, { y: 24 });
    if (!longest) return;
    s.tl.fromTo(".cpanel", { autoAlpha: 0, y: 160 }, { autoAlpha: 1, y: 0, duration: 0.6 }, 1.35);
    // The playhead runs the length of the call; your lane fills behind it.
    const play = { duration: 1.9, ease: "power1.inOut" };
    s.tl.fromTo(".lane-b", { width: 0 }, { width: TRACK_W, ...play }, 1.85);
    s.tl.fromTo(".phead", { left: TRACK_X }, { left: TRACK_X + TRACK_W, ...play }, 1.85);
    s.tl.set(".phead", { autoAlpha: 1 }, 1.85);
    s.tl.set(".phead", { autoAlpha: 0 }, 1.85 + play.duration);
    s.count(s.q(".dur")[0], longest, 1.85, play.duration, duration, { ease: play.ease });
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div data-part="kick" className="kick">
            Your calls
          </div>
        </Zone>
        {/* The hours are the moment; the longest call's panel is the foot. */}
        <Zone at="middle">
          <div className="c-row">
            <div className="c-big disp" style={{ fontSize: size }}>
              {hours}
            </div>
            <div className="c-unit disp">{hours === "1" ? "hour" : "hours"}</div>
          </div>
          <div data-part="sub" className="line" style={{ top: 730 }}>
            on call, across {count(calls.count)} {calls.count === 1 ? "call" : "calls"} you were in.
          </div>
        </Zone>
        {longest ? (
          <Zone at="bottom">
            <div className="cpanel">
              <div className="k2">LONGEST CALL YOU WERE IN</div>
              {calls.longest_date ? <div className="when">{dayOf(calls.longest_date)}</div> : null}
              <div className="dur disp">{duration(longest)}</div>
              {/* Only your own time in the call is known, so there is one lane: yours. */}
              <div className="abs mono" style={{ left: 48, top: LANE_Y + 8, fontSize: 28, fontWeight: 700, letterSpacing: "0.06em", color: "var(--wr-yellow)" }}>
                YOU
              </div>
              <div className="lane-t" style={{ top: LANE_Y }}>
                <div className="lane-b" style={{ width: TRACK_W, background: "var(--wr-yellow)" }} />
              </div>
              {marks(longest).map((mark) => (
                <div key={mark.label} className="axis" style={{ left: TRACK_X + mark.at * TRACK_W }}>
                  {mark.label}
                </div>
              ))}
              <div className="phead wr-hide" style={{ left: TRACK_X + TRACK_W, top: LANE_Y - 60, height: 164 }} />
            </div>
          </Zone>
        ) : null}
      </Scene>
    </div>
  );
}
